import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React, { FC } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { GLYPH_COLORS, TriangleDownGlyph, TriangleUpGlyph } from '../primitive-glyphs/glyphs'
import { Text } from '../text'

export type TableCellAlign = 'left' | 'center' | 'right'
export type TableSortDirection = 'off' | 'up' | 'down'

export type TableHeaderCellProps = {
    label: string
    /** Content alignment — numbers/times right, codes/text left, icons centered. */
    align?: TableCellAlign
    /** Sort state. The arrow is only shown when sorted (`up`/`down`). */
    sort?: TableSortDirection
    className?: string
    /** Override the label colour (e.g. `text-aos-text-default`); defaults to `content/link`. */
    labelClassName?: string
    testID?: string
}

const tableHeaderCell = tva({
    base: 'min-h-9 flex-row items-center gap-1 px-1',
    variants: {
        align: {
            left: 'justify-start',
            center: 'justify-center',
            right: 'justify-end',
        },
    },
})

// Long captions ("TOT DELAY", "~ELDT / ALDT") are wider than their column, so the
// label wraps instead of overrunning its neighbour; `text-*` aligns the wrapped
// lines the same way the row aligns the label as a whole.
const headerLabelAlign = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
} as const

/**
 * Sortable table header cell (Figma node 7714:127157 — `sort=off/up/down`). A
 * row at least 36px tall holding a Manrope P2 medium label in `content/link`; when sorted it
 * shows a solid triangle on the side of the label nearest the content edge —
 * right-aligned headers put the arrow to the **left** of the label, left/center
 * to the **right** — matching the table-behaviour spec. (AOS-629)
 */
export const TableHeaderCell: FC<TableHeaderCellProps> = ({
    label,
    align = 'left',
    sort = 'off',
    className,
    labelClassName,
    testID,
}) => {
    const arrow =
        sort === 'up' ? (
            <TriangleUpGlyph size={16} color={GLYPH_COLORS.link} />
        ) : sort === 'down' ? (
            <TriangleDownGlyph size={16} color={GLYPH_COLORS.link} />
        ) : null
    // Arrow hugs the content edge: leading for right-aligned, trailing otherwise.
    const arrowLeading = align === 'right'

    return (
        <Box className={tableHeaderCell({ align, class: className })} testID={testID}>
            {arrowLeading && arrow}
            <Text
                size='body'
                weight='medium'
                className={twMerge(
                    'min-w-0 shrink text-aos-text-link',
                    headerLabelAlign[align],
                    labelClassName,
                )}
            >
                {label}
            </Text>
            {!arrowLeading && arrow}
        </Box>
    )
}

TableHeaderCell.displayName = 'TableHeaderCell'
